import { useEffect, useState } from "react";
import { Modal } from "antd";
import { HiOutlinePencil, HiOutlineTrash, HiOutlinePlus } from "react-icons/hi";
import PageContainer from "../../../components/page-container";
import Button from "../../../components/button/button";
import Message from "../../../components/toast-message";
import { useAppInfo } from "../../../helpers/hooks/common-hook";
import {
	getPromptConfigTools,
	createPromptConfigTool,
	updatePromptConfigTool,
	deletePromptConfigTool,
} from "../../../services/prompt-ai";
import { AGENT_NAVIGATION } from "../../agent-manage/consts/navigations";
import CodehookPanel from "./codehook-panel";
import JsonPanel from "./json-panel";
import KbPanel from "./kb-panel";

// ─── Constants ────────────────────────────────────────────────────────────────
const PARAM_TYPES = ["string", "number", "boolean", "array", "object"];

const TOOL_TYPES = [
	{ value: "codehook", label: "Codehook" },
	{ value: "json", label: "JSON" },
	{ value: "kb", label: "Knowledge Base" },
];

// ─── Empty shapes ─────────────────────────────────────────────────────────────
const emptyParam = () => ({
	_key: Date.now() + Math.random(),
	name: "",
	type: "string",
	description: "",
	required: false,
});

const emptyForm = () => ({
	name: "",
	type: "",
	description: "",
	parameters: [],
	toolData: {},
});

// ─── ParameterRow ─────────────────────────────────────────────────────────────
function ParameterRow({ param, onChange, onDelete }) {
	const field = (key, value) => onChange({ ...param, [key]: value });

	return (
		<div className="flex flex-col gap-2 p-3 border border-gray-200 rounded-lg bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
			<div className="flex gap-2 items-start">
				{/* Name */}
				<div className="flex-1">
					<label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Name</label>
					<input
						type="text"
						value={param.name}
						onChange={(e) => field("name", e.target.value)}
						placeholder="param_name"
						className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:border-gray-600 dark:text-white"
					/>
				</div>

				{/* Type */}
				<div className="w-28">
					<label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Type</label>
					<select
						value={param.type}
						onChange={(e) => field("type", e.target.value)}
						className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:border-gray-600 dark:text-white"
					>
						{PARAM_TYPES.map((t) => (
							<option key={t} value={t}>{t}</option>
						))}
					</select>
				</div>

				{/* Required */}
				<div className="flex flex-col items-center pt-5">
					<label className="text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Req.</label>
					<input
						type="checkbox"
						checked={param.required}
						onChange={(e) => field("required", e.target.checked)}
						className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
					/>
				</div>

				{/* Delete */}
				<button
					type="button"
					onClick={onDelete}
					className="mt-5 text-red-400 hover:text-red-600 transition-colors"
					title="Remove parameter"
				>
					<HiOutlineTrash className="h-4 w-4" />
				</button>
			</div>

			{/* Description */}
			<div>
				<label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">Description</label>
				<input
					type="text"
					value={param.description}
					onChange={(e) => field("description", e.target.value)}
					placeholder="Describe this parameter..."
					className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-800 dark:border-gray-600 dark:text-white"
				/>
			</div>
		</div>
	);
}

// ─── ToolCard ─────────────────────────────────────────────────────────────────
function ToolCard({ tool, onEdit, onDelete }) {
	const paramCount = Object.keys(tool.parameters || {}).length;
	const typeLabel = TOOL_TYPES.find((t) => t.value === tool.type)?.label || tool.type || "";

	return (
		<div className="flex flex-row items-start justify-between border border-gray-200 rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700 px-4 py-3 hover:shadow-sm transition-shadow">
			<div className="flex-1 min-w-0 pr-4">
				<div className="flex items-center gap-2">
					<p className="text-sm font-semibold text-gray-800 dark:text-white font-mono truncate">{tool.name}</p>
					{typeLabel && (
						<span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 px-2 py-0.5 rounded-full">
							{typeLabel}
						</span>
					)}
				</div>
				{tool.description && (
					<p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{tool.description}</p>
				)}
				<p className="text-xs text-gray-400 mt-1">
					{paramCount} parameter{paramCount !== 1 ? "s" : ""}
				</p>
			</div>
			<div className="flex items-center gap-2 flex-shrink-0">
				<button
					type="button"
					onClick={() => onEdit(tool)}
					className="p-1.5 text-gray-500 hover:text-primary transition-colors rounded"
					title="Edit tool"
				>
					<HiOutlinePencil className="h-4 w-4" />
				</button>
				<button
					type="button"
					onClick={() => onDelete(tool)}
					className="p-1.5 text-gray-500 hover:text-red-500 transition-colors rounded"
					title="Delete tool"
				>
					<HiOutlineTrash className="h-4 w-4" />
				</button>
			</div>
		</div>
	);
}

// ─── DeleteConfirmModal ───────────────────────────────────────────────────────
function DeleteConfirmModal({ tool, onConfirm, onCancel, processing }) {
	return (
		<Modal
			title={<span className="font-semibold text-gray-800">Delete Tool</span>}
			open={!!tool}
			onCancel={onCancel}
			footer={
				<div className="flex justify-end gap-2">
					<Button text="Cancel" color="default" onClick={onCancel} disabled={processing} />
					<Button text="Delete" color="error" onClick={onConfirm} loading={processing} />
				</div>
			}
		>
			<p className="text-sm text-gray-600 mt-2">
				Are you sure you want to delete <span className="font-mono font-semibold">{tool?.name}</span>? This
				action cannot be undone.
			</p>
		</Modal>
	);
}

// ─── Helper: convert tool parameters map → array for the form ─────────────────
function paramsMapToArray(parametersMap) {
	if (!parametersMap || typeof parametersMap !== "object") return [];
	return Object.entries(parametersMap).map(([name, def]) => ({
		_key: Date.now() + Math.random(),
		name,
		type: def.type || "string",
		description: def.description || "",
		required: !!def.required,
	}));
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function AgentTools() {
	const { appId } = useAppInfo();

	const [tools, setTools] = useState([]);
	const [loadState, setLoadState] = useState({ processing: false, failed: false });

	// Modal state
	const [modalOpen, setModalOpen] = useState(false);
	const [editingTool, setEditingTool] = useState(null);
	const [editingForm, setEditingForm] = useState(emptyForm());

	// Delete state
	const [deletingTool, setDeletingTool] = useState(null);
	const [deleteProcessing, setDeleteProcessing] = useState(false);

	async function fetchTools() {
		setLoadState({ processing: true, failed: false });
		const data = await getPromptConfigTools(appId);
		setTools(Array.isArray(data) ? data : []);
		setLoadState({ processing: false, failed: false });
	}

	useEffect(() => {
		if (appId) fetchTools();
	}, [appId]);

	const handleAddNew = () => {
		setEditingTool(null);
		setEditingForm(emptyForm());
		setModalOpen(true);
	};

	const handleEdit = (tool) => {
		setEditingTool(tool);
		setEditingForm({
			name: tool.name || "",
			type: tool.type || "",
			description: tool.description || "",
			parameters: paramsMapToArray(tool.parameters),
			toolData: tool.toolData || {},
		});
		setModalOpen(true);
	};

	const handleModalClose = () => {
		setModalOpen(false);
		setEditingTool(null);
	};

	const handleSaved = () => {
		setModalOpen(false);
		setEditingTool(null);
		fetchTools();
	};

	const handleDeleteClick = (tool) => setDeletingTool(tool);

	const handleDeleteConfirm = async () => {
		if (!deletingTool) return;
		setDeleteProcessing(true);
		await deletePromptConfigTool(appId, deletingTool.id);
		setDeleteProcessing(false);
		setDeletingTool(null);
		fetchTools();
	};

	return (
		<PageContainer
			title="Tools"
			subtitle="Define tools that the agent can invoke during conversations"
			navigation={AGENT_NAVIGATION}
			headerAction={<Button text="Add Tool" onClick={handleAddNew} />}
		>
			<div className="mt-6 px-1">
				{loadState.processing ? (
					<div className="flex justify-center items-center py-16">
						<p className="text-sm text-gray-400">Loading tools…</p>
					</div>
				) : tools.length === 0 ? (
					<div className="flex flex-col items-center justify-center py-16 text-center">
						<p className="text-gray-500 text-sm font-medium">No tools defined yet</p>
						<p className="text-gray-400 text-xs mt-1 mb-4">
							Add a tool so the agent can call external functions during conversations.
						</p>
						<Button text="Add your first tool" onClick={handleAddNew} />
					</div>
				) : (
					<div className="flex flex-col gap-3">
						{tools.map((tool) => (
							<ToolCard
								key={tool.id}
								tool={tool}
								onEdit={handleEdit}
								onDelete={handleDeleteClick}
							/>
						))}
					</div>
				)}
			</div>

			{/* Add / Edit modal */}
			<ToolModalWithForm
				open={modalOpen}
				toolId={editingTool?.id}
				initialForm={editingForm}
				onClose={handleModalClose}
				onSaved={handleSaved}
				appId={appId}
			/>

			{/* Delete confirmation */}
			<DeleteConfirmModal
				tool={deletingTool}
				onConfirm={handleDeleteConfirm}
				onCancel={() => setDeletingTool(null)}
				processing={deleteProcessing}
			/>
		</PageContainer>
	);
}

// ─── ToolModalWithForm ────────────────────────────────────────────────────────
function ToolModalWithForm({ open, toolId, initialForm, onClose, onSaved, appId }) {
	const isEdit = !!toolId;

	const [form, setForm] = useState(initialForm || emptyForm());
	const [saveState, setSaveState] = useState({ processing: false, failed: false });

	// Sync when modal opens / toolId changes
	useEffect(() => {
		if (open) {
			setForm(initialForm || emptyForm());
			setSaveState({ processing: false, failed: false });
		}
	}, [open, toolId]);

	const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));
	const setToolData = (toolData) => setForm((f) => ({ ...f, toolData }));

	// Parameter helpers
	const addParam = () => setForm((f) => ({ ...f, parameters: [...f.parameters, emptyParam()] }));
	const updateParam = (idx, updated) =>
		setForm((f) => {
			const params = [...f.parameters];
			params[idx] = updated;
			return { ...f, parameters: params };
		});
	const removeParam = (idx) =>
		setForm((f) => ({ ...f, parameters: f.parameters.filter((_, i) => i !== idx) }));

	// Build API payload
	const buildPayload = () => {
		const parameters = {};
		form.parameters.forEach((p) => {
			if (p.name.trim()) {
				parameters[p.name.trim()] = {
					type: p.type,
					description: p.description,
					required: p.required,
				};
			}
		});
		return {
			name: form.name.trim(),
			type: form.type,
			description: form.description.trim(),
			parameters,
			toolData: form.toolData || {},
		};
	};

	const handleSave = async () => {
		if (!form.name.trim()) return;
		setSaveState({ processing: true, failed: false });
		const payload = buildPayload();
		const result = isEdit
			? await updatePromptConfigTool(appId, toolId, payload)
			: await createPromptConfigTool(appId, payload);

		if (result?.status === 200 || result?.status === 201) {
			onSaved();
		} else {
			setSaveState({ processing: false, failed: true });
		}
	};

	return (
		<Modal
			title={<span className="font-semibold text-gray-800">{isEdit ? "Edit Tool" : "Add Tool"}</span>}
			open={open}
			onCancel={onClose}
			width={660}
			destroyOnClose
			footer={
				<div className="flex justify-end gap-2">
					<Button text="Cancel" color="default" onClick={onClose} disabled={saveState.processing} />
					<Button text={isEdit ? "Update" : "Save"} onClick={handleSave} loading={saveState.processing} />
				</div>
			}
		>
			<div className="flex flex-col gap-4 mt-4 max-h-[70vh] overflow-y-auto pr-1">
				{saveState.failed && (
					<Message text="Failed to save tool. Please try again." type="failure" />
				)}

				{/* Name */}
				<div>
					<label className="block text-sm font-semibold text-gray-800 dark:text-white mb-1">
						Tool Name <span className="text-red-500">*</span>
					</label>
					<p className="text-xs text-gray-500 mb-1">Use snake_case, e.g. get_current_time</p>
					<input
						type="text"
						value={form.name}
						onChange={(e) => setField("name", e.target.value)}
						placeholder="tool_name"
						className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:border-gray-600 dark:text-white"
					/>
				</div>

				{/* Description */}
				<div>
					<label className="block text-sm font-semibold text-gray-800 dark:text-white mb-1">Description</label>
					<p className="text-xs text-gray-500 mb-1">Explain what this tool does and when to use it.</p>
					<textarea
						value={form.description}
						onChange={(e) => setField("description", e.target.value)}
						rows={3}
						placeholder="Returns the current date and time..."
						className="w-full resize-none border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:border-gray-600 dark:text-white"
					/>
				</div>

				{/* Parameters – hidden for kb and json types */}
				{!["kb", "json"].includes(form.type) && <div>
					<div className="flex justify-between items-center mb-2">
						<div>
							<p className="text-sm font-semibold text-gray-800 dark:text-white">Parameters</p>
							<p className="text-xs text-gray-500">Define the inputs this tool accepts.</p>
						</div>
						<button
							type="button"
							onClick={addParam}
							className="flex items-center gap-1 text-sm text-primary hover:text-secondary font-medium transition-colors"
						>
							<HiOutlinePlus className="h-4 w-4" />
							Add Parameter
						</button>
					</div>

					{form.parameters.length === 0 ? (
						<p className="text-xs text-gray-400 italic py-2">No parameters added yet.</p>
					) : (
						<div className="flex flex-col gap-2 pr-1">
							{form.parameters.map((p, idx) => (
								<ParameterRow
									key={p._key}
									param={p}
									onChange={(updated) => updateParam(idx, updated)}
									onDelete={() => removeParam(idx)}
								/>
							))}
						</div>
					)}
				</div>}

				{/* Type */}
				<div>
					<label className="block text-sm font-semibold text-gray-800 dark:text-white mb-1">Type</label>
					<select
						value={form.type}
						onChange={(e) => {
							// Reset toolData when type changes
							setForm((f) => ({ ...f, type: e.target.value, toolData: {} }));
						}}
						className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:border-gray-600 dark:text-white"
					>
						<option value="">Select type…</option>
						{TOOL_TYPES.map((t) => (
							<option key={t.value} value={t.value}>{t.label}</option>
						))}
					</select>
				</div>

				{/* ── Type-specific panels ── */}
				{form.type === "codehook" && (
					<CodehookPanel toolData={form.toolData} setToolData={setToolData} />
				)}

				{form.type === "json" && (
					<JsonPanel toolData={form.toolData} setToolData={setToolData} />
				)}

				{form.type === "kb" && (
					<KbPanel toolData={form.toolData} setToolData={setToolData} />
				)}

			</div>
		</Modal>
	);
}
